// Сессия пользователя: проверка /api/auth/me, вход, выход. 401 из любого запроса → экран входа.
import { useSyncExternalStore } from 'react'
import { ApiError, api, setUnauthorizedHandler } from './api.ts'
import { stopLive } from './store.ts'
import type { User } from './types.ts'

export interface AuthState {
  status: 'loading' | 'anon' | 'authed'
  user: User | null
}

let state: AuthState = { status: 'loading', user: null }
const listeners = new Set<() => void>()

function set(next: AuthState): void {
  state = next
  for (const l of listeners) l()
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function useAuth(): AuthState {
  return useSyncExternalStore(subscribe, () => state)
}

export function currentUser(): User | null {
  return state.user
}

setUnauthorizedHandler(() => {
  if (state.status === 'authed') {
    stopLive()
    set({ status: 'anon', user: null })
  } else if (state.status === 'loading') {
    set({ status: 'anon', user: null })
  }
})

export async function checkAuth(): Promise<void> {
  try {
    const r = await api.get<{ user: User }>('/api/auth/me', { ignore401: true })
    set({ status: 'authed', user: r.user })
  } catch (e) {
    if (e instanceof ApiError && e.status !== 401 && e.status !== 0) {
      console.warn('auth check failed', e)
    }
    set({ status: 'anon', user: null })
  }
}

export async function login(email: string, password: string): Promise<void> {
  const r = await api.post<{ user: User }>('/api/auth/login', { email, password }, { ignore401: true })
  set({ status: 'authed', user: r.user })
}

export async function logout(): Promise<void> {
  try {
    await api.post('/api/auth/logout', {}, { ignore401: true })
  } finally {
    stopLive()
    set({ status: 'anon', user: null })
  }
}
